import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  type AppCheck,
} from 'firebase/app-check';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { startAnalytics } from './analytics';
import { readFirebaseEnv, type FirebaseEnv } from './env';

/**
 * Firebase initialisation, done once.
 *
 * Assembled eagerly at module load and exported as a frozen bundle rather than
 * being fetched through `getApp()` at each call site — that keeps the emulator
 * wiring and the App Check registration in exactly one place, where they cannot
 * be half-applied.
 */

export interface FirebaseClient {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly db: Firestore;
  readonly functions: Functions;
  readonly storage: FirebaseStorage;
  readonly appCheck: AppCheck | null;
  readonly env: FirebaseEnv;
}

/**
 * `us-central1` matches the default Cloud Functions region. Callables must be
 * created against the same region they were deployed to, or every call fails with
 * a CORS error that looks like a networking problem rather than a config one.
 */
const FUNCTIONS_REGION = 'us-central1';

let client: FirebaseClient | null = null;

export const initializeFirebase = (env: FirebaseEnv = readFirebaseEnv()): FirebaseClient => {
  if (client !== null) return client;

  const app = initializeApp({
    apiKey: env.apiKey,
    authDomain: env.authDomain,
    projectId: env.projectId,
    storageBucket: env.storageBucket,
    messagingSenderId: env.messagingSenderId,
    appId: env.appId,
    ...(env.measurementId !== null ? { measurementId: env.measurementId } : {}),
  });

  const appCheck = registerAppCheck(app, env);

  const auth = getAuth(app);

  /**
   * Persistent local cache with multi-tab coordination.
   *
   * Two reasons this matters here rather than being a nicety. First, the runtime
   * configuration and the holiday calendar are read on every screen; serving them
   * from cache turns a cold navigation into a warm one. Second, people keep this
   * portal open in a background tab for days — the multi-tab manager stops two
   * tabs fighting over the same listener and duplicating reads, which at 500 users
   * is a real cost rather than a rounding error.
   */
  const db = initializeFirestore(
    app,
    {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
      ignoreUndefinedProperties: true,
      /*
       * Long polling, forced rather than auto-detected.
       *
       * Firestore's default transport is a streaming WebChannel, and corporate
       * proxies routinely break it — they buffer the response and the stream never
       * opens. Firestore has no timeout for that: it retries silently and a
       * `getDocs` simply never returns, which is the spinner that turned forever on
       * every screen. The tell was that a reload was instant: by then the SDK had
       * given up on the network and was answering from the persisted cache, so what
       * looked like "it works the second time" was the app running offline.
       *
       * `experimentalAutoDetectLongPolling` is already the default and is exactly
       * what failed here — detection needs the first probe to fail cleanly, and a
       * proxy that hangs instead of refusing never gives it that. Forcing it costs
       * a little latency and a few more requests; it does not change semantics.
       *
       * If the network is ever known good, this is one line to remove.
       */
      experimentalForceLongPolling: true,
    },
    // The third argument is the database id, and omitting it is not a no-op: it
    // silently selects `(default)`, which in this project belongs to an earlier
    // app. See the note on `databaseId` in ./env.ts.
    env.databaseId,
  );

  const functions = getFunctions(app, FUNCTIONS_REGION);
  const storage = getStorage(app);

  if (env.useEmulators) connectEmulators({ auth, db, functions, storage });

  /*
   * Analytics va detrás y sin esperar: carga su SDK aparte y no tiene por qué
   * retrasar la primera pantalla. Sin `measurementId` —que es como está hoy— no
   * llega ni a importarse. Ver `./analytics.ts`.
   */
  void startAnalytics(app, env);

  client = Object.freeze({ app, auth, db, functions, storage, appCheck, env });
  return client;
};

/** The initialised client. Throws if called before `initializeFirebase`. */
export const firebase = (): FirebaseClient => {
  if (client === null) {
    throw new Error(
      'Firebase no está inicializado. Llama a initializeFirebase() en el arranque (src/main.tsx).',
    );
  }
  return client;
};

/**
 * App Check: what actually stops a script on another origin from using the
 * public `apiKey` against this backend.
 *
 * Absent a site key the app still starts, with a console warning. That is
 * deliberate for local development — requiring a reCAPTCHA key to run `npm run
 * dev` would be friction with no security benefit, since the emulators hold no
 * real data. In production the key must be set and enforcement switched on in the
 * console; docs/FIREBASE_SETUP.md covers it.
 */
const registerAppCheck = (app: FirebaseApp, env: FirebaseEnv): AppCheck | null => {
  if (env.appCheckSiteKey === null) {
    console.warn(
      '[firebase] App Check está desactivado: falta VITE_FIREBASE_APPCHECK_SITE_KEY. ' +
        'Aceptable en desarrollo; en producción deja el backend expuesto.',
    );
    return null;
  }

  if (env.appCheckDebugToken !== null) {
    // Read by the App Check SDK at registration. Only ever set from a local
    // .env.local; a production build with this set would hand out a bypass token.
    (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      env.appCheckDebugToken;
  }

  try {
    return initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(env.appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (cause) {
    // A malformed key must not stop the app booting; the backend will reject the
    // requests, which is a clearer signal than a white screen.
    console.error('[firebase] No se pudo registrar App Check.', cause);
    return null;
  }
};

const EMULATOR_HOST = '127.0.0.1';
const EMULATOR_PORTS = { auth: 9099, firestore: 8080, functions: 5001, storage: 9199 } as const;

const connectEmulators = (services: {
  auth: Auth;
  db: Firestore;
  functions: Functions;
  storage: FirebaseStorage;
}): void => {
  connectAuthEmulator(services.auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(services.db, EMULATOR_HOST, EMULATOR_PORTS.firestore);
  connectFunctionsEmulator(services.functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
  connectStorageEmulator(services.storage, EMULATOR_HOST, EMULATOR_PORTS.storage);

  console.warn(
    '[firebase] Conectado a los emuladores locales. ' +
      'El emulador de Auth acepta cualquier correo, así que las blocking functions ' +
      'NO validan el dominio igual que en producción: prueba el cerco corporativo ' +
      'contra el proyecto -dev real antes de publicar.',
  );
};

/** Test seam: lets a suite reset the singleton between cases. */
export const resetFirebaseForTesting = (): void => {
  client = null;
};

/** Firestore collection names, in one place so a typo is a compile error. */
export const COLLECTIONS = {
  companies: 'companies',
  areas: 'areas',
  subAreas: 'subAreas',
  people: 'people',
  personLeaders: 'personLeaders',
  vacationRequests: 'vacationRequests',
  requestApprovals: 'requestApprovals',
  requestWorkItems: 'requestWorkItems',
  permissionRequests: 'permissionRequests',
  incapacities: 'incapacities',
  emergencyContacts: 'emergencyContacts',
  personalRecords: 'personalRecords',
  dataConfirmations: 'dataConfirmations',
  holidays: 'holidays',
  auditLog: 'auditLog',
  balances: 'balances',
  config: 'config',
  emailTemplateOverrides: 'emailTemplateOverrides',
  /** Lo que cada área cambia sobre incapacidades y permisos. Ver `AreaCatalog`. */
  areaCatalogs: 'areaCatalogs',
  /** Los cumpleaños de Kn·Blog, un documento por mes y sin año. */
  birthdayMonths: 'birthdayMonths',
  system: 'system',
  drafts: 'drafts',
  counters: 'counters',
} as const;

/** Documents inside the `config` collection. */
export const CONFIG_DOCS = {
  identity: 'identity',
  rules: 'rules',
  catalogs: 'catalogs',
  featureFlags: 'featureFlags',
  rolePermissions: 'rolePermissions',
  emailTemplates: 'emailTemplates',
} as const;

export const SYSTEM_DOCS = { runtime: 'runtime' } as const;
